import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import api from "../lib/api";

const ResetPasswordPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const token = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("token") || "";
  }, [location.search]);

  const handleReset = async (event) => {
    event.preventDefault();
    if (!token) {
      toast.error("Reset token is missing.");
      return;
    }
    if (!password.trim()) {
      toast.error("Enter a new password.");
      return;
    }
    try {
      setSubmitting(true);
      await api.post("/auth/reset-password", { token, new_password: password });
      toast.success("Password updated. Please log in.");
      navigate("/auth");
    } catch (error) {
      toast.error("Unable to reset password.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" data-testid="reset-password-page">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-semibold" data-testid="reset-password-title">
            Reset password
          </h1>
          <p className="text-sm text-muted-foreground" data-testid="reset-password-subtitle">
            Create a new password for your account.
          </p>
        </div>
        {!token && (
          <div className="rounded-xl border border-border bg-card p-4 text-sm text-muted-foreground" data-testid="reset-password-missing-token">
            Reset token is missing. Please use the link from your email.
          </div>
        )}
        <form className="space-y-4" onSubmit={handleReset} data-testid="reset-password-form">
          <div className="space-y-2">
            <label className="text-sm font-medium" data-testid="reset-password-label">
              New password
            </label>
            <Input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              data-testid="reset-password-input"
              required
            />
          </div>
          <div className="flex items-center gap-2 text-xs" data-testid="reset-password-toggle-row">
            <Switch checked={showPassword} onCheckedChange={setShowPassword} data-testid="reset-password-toggle" />
            <span data-testid="reset-password-toggle-label">Show password</span>
          </div>
          <Button type="submit" disabled={submitting} data-testid="reset-password-submit">
            {submitting ? "Updating..." : "Update password"}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
